import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Modal, Button, Form, Badge, Tabs, Tab } from "react-bootstrap";
import { FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";
import ConfirmModal from "../components/ConfirmModal";
import api from "../services/api";

const Services = () => {
  const [activeTab, setActiveTab] = useState("services");
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({
    category_id: "",
    name: "",
    description: "",
    price: "",
    duration_minutes: "30",
    is_active: 1,
    is_loyalty_reward: 0,
  });

  // Promos & Vouchers
  const [promos, setPromos] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [showVoucherModal, setShowVoucherModal] = useState(false);
  const [editPromo, setEditPromo] = useState(null);
  const [editVoucher, setEditVoucher] = useState(null);
  const [promoForm, setPromoForm] = useState({
    name: "",
    description: "",
    type: "percentage",
    value: "",
    min_purchase: "0",
    applies_to: "all",
    start_date: "",
    end_date: "",
    is_active: 1,
  });
  const [voucherForm, setVoucherForm] = useState({
    code: "",
    promo_id: "",
    discount_type: "percentage",
    discount_value: "",
    min_purchase: "0",
    max_uses: "1",
    valid_from: "",
    valid_until: "",
    is_active: 1,
  });

  // Service Category Modal
  const [showCatModal, setShowCatModal] = useState(false);
  const [editCat, setEditCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: "", description: "" });

  // Vehicle Types
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [showVTModal, setShowVTModal] = useState(false);
  const [editVT, setEditVT] = useState(null);
  const [vtForm, setVtForm] = useState({
    name: "",
    requires_plate: 1,
    sort_order: 0,
    is_active: 1,
  });

  // Loyalty Rules
  const [loyaltyRules, setLoyaltyRules] = useState([]);
  const [products, setProducts] = useState([]);
  const [loyaltyEnabled, setLoyaltyEnabled] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editRule, setEditRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    name: "",
    vehicle_category_id: "",
    trigger_service_ids: "",
    required_count: "10",
    reward_service_id: "",
    redemption_mode: "manual",
    reward_item_type: "service",
    reward_product_id: "",
  });
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ show: true, title, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, show: false, onConfirm: null }));
  };

  useEffect(() => {
    loadData();
    loadVehicleTypes();
    loadLoyaltyRules();
  }, []);

  const loadData = async () => {
    try {
      const [svcRes, catRes, promoRes, voucherRes, prodRes] = await Promise.all([
        api.get("/services"),
        api.get("/services/categories"),
        api.get("/promos"),
        api.get("/promos/vouchers"),
        api.get("/products"),
      ]);
      setServices(svcRes.data);
      setCategories(catRes.data);
      setPromos(promoRes.data);
      setVouchers(voucherRes.data);
      setProducts(prodRes.data);
    } catch (error) {
      toast.error("Gagal memuat data");
    }
  };

  const loadVehicleTypes = async () => {
    try {
      const res = await api.get("/settings/vehicle-types");
      setVehicleTypes(res.data);
    } catch (error) {
      console.error("Gagal memuat jenis kendaraan");
    }
  };

  // ========== Service Category Handlers ==========
  const openCatModal = (cat = null) => {
    setEditCat(cat);
    setCatForm(
      cat
        ? { name: cat.name, description: cat.description || "" }
        : { name: "", description: "" },
    );
    setShowCatModal(true);
  };

  const handleSaveCat = async () => {
    if (!catForm.name) {
      toast.error("Nama kategori wajib diisi");
      return;
    }
    try {
      if (editCat) {
        await api.put(`/services/categories/${editCat.id}`, catForm);
        toast.success("Kategori diupdate");
      } else {
        await api.post("/services/categories", catForm);
        toast.success("Kategori ditambahkan");
      }
      setShowCatModal(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menyimpan");
    }
  };

  const handleDeleteCat = (cat) => {
    openConfirm(
      "Hapus Kategori",
      `Hapus kategori "${cat.name}"? Layanan dengan kategori ini akan kehilangan kategori.`,
      async () => {
        closeConfirm();
        try {
          await api.delete(`/services/categories/${cat.id}`);
          toast.success("Kategori dihapus");
          loadData();
        } catch (e) {
          toast.error(e.response?.data?.message || "Gagal menghapus");
        }
      },
    );
  };

  const loadLoyaltyRules = async () => {
    try {
      const [rulesRes, settRes] = await Promise.all([
        api.get("/settings/loyalty-rules"),
        api.get("/settings"),
      ]);
      setLoyaltyRules(rulesRes.data);
      setLoyaltyEnabled(settRes.data.loyalty_enabled === "1");
    } catch (error) {
      console.error("Gagal memuat loyalty rules");
    }
  };

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

  // ========== Service Handlers ==========
  const handleSubmit = async () => {
    if (!form.name || !form.price) {
      toast.error("Nama dan harga wajib diisi");
      return;
    }
    try {
      let serviceId = editItem?.id;
      if (editItem) {
        await api.put(`/services/${editItem.id}`, {
          ...form,
          is_active: form.is_active ?? 1,
        });
      } else {
        const res = await api.post("/services", form);
        serviceId = res.data.id;
      }
      toast.success("Layanan berhasil disimpan");
      setShowModal(false);
      setEditItem(null);
      setForm({
        category_id: "",
        name: "",
        description: "",
        price: "",
        duration_minutes: "30",
        is_active: 1,
        is_loyalty_reward: 0,
      });
      loadData();
    } catch (error) {
      toast.error("Gagal menyimpan");
    }
  };

  const handleEdit = (svc) => {
    setEditItem(svc);
    setForm({
      category_id: svc.category_id || "",
      name: svc.name,
      description: svc.description || "",
      price: svc.price,
      duration_minutes: svc.duration_minutes,
      is_active: svc.is_active,
      is_loyalty_reward: svc.is_loyalty_reward || 0,
    });
    setShowModal(true);
  };

  const openAddService = () => {
    setEditItem(null);
    setForm({
      category_id: "",
      name: "",
      description: "",
      price: "",
      duration_minutes: "30",
      is_active: 1,
      is_loyalty_reward: 0,
    });
    setShowModal(true);
  };

  const toggleServiceActive = async (svc) => {
    try {
      await api.put(`/services/${svc.id}/toggle`);
      toast.success(
        `Layanan ${svc.is_active ? "dinonaktifkan" : "diaktifkan"}`,
      );
      loadData();
    } catch (error) {
      toast.error("Gagal mengubah status layanan");
    }
  };

  const handleDeleteService = (svc) => {
    openConfirm("Hapus Layanan", `Hapus layanan "${svc.name}"?`, async () => {
      closeConfirm();
      try {
        await api.delete(`/services/${svc.id}`);
        toast.success("Layanan berhasil dihapus");
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.message || "Gagal menghapus layanan");
      }
    });
  };

  // ========== Promo Handlers ==========
  const handlePromoSubmit = async () => {
    if (!promoForm.name) {
      toast.error("Nama promo wajib diisi");
      return;
    }
    try {
      if (editPromo) {
        await api.put(`/promos/${editPromo.id}`, promoForm);
        toast.success("Promo berhasil diupdate");
      } else {
        await api.post("/promos", promoForm);
        toast.success("Promo berhasil ditambahkan");
      }
      setShowPromoModal(false);
      setEditPromo(null);
      setPromoForm({
        name: "",
        description: "",
        type: "percentage",
        value: "",
        min_purchase: "0",
        applies_to: "all",
        start_date: "",
        end_date: "",
        is_active: 1,
      });
      loadData();
    } catch (error) {
      toast.error("Gagal menyimpan promo");
    }
  };

  const handleEditPromo = (promo) => {
    setEditPromo(promo);
    setPromoForm({
      name: promo.name,
      description: promo.description || "",
      type: promo.type,
      value: promo.value,
      min_purchase: promo.min_purchase || "0",
      applies_to: promo.applies_to,
      start_date: promo.start_date || "",
      end_date: promo.end_date || "",
      is_active: promo.is_active,
    });
    setShowPromoModal(true);
  };

  const handleDeletePromo = (promo) => {
    openConfirm("Hapus Promo", `Hapus promo "${promo.name}"?`, async () => {
      closeConfirm();
      try {
        await api.delete(`/promos/${promo.id}`);
        toast.success("Promo berhasil dihapus");
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.message || "Gagal menghapus promo");
      }
    });
  };

  const togglePromoActive = async (promo) => {
    try {
      await api.put(`/promos/${promo.id}/toggle`);
      toast.success(
        `Promo ${promo.is_active ? "dinonaktifkan" : "diaktifkan"}`,
      );
      loadData();
    } catch (error) {
      toast.error("Gagal mengubah status promo");
    }
  };

  const toggleVoucherActive = async (v) => {
    try {
      await api.put(`/promos/vouchers/${v.id}/toggle`);
      toast.success(`Voucher ${v.is_active ? "dinonaktifkan" : "diaktifkan"}`);
      loadData();
    } catch (error) {
      toast.error("Gagal mengubah status voucher");
    }
  };

  const handleVoucherSubmit = async () => {
    if (!voucherForm.code) {
      toast.error("Kode voucher wajib diisi");
      return;
    }
    try {
      if (editVoucher) {
        await api.put(`/promos/vouchers/${editVoucher.id}`, voucherForm);
        toast.success("Voucher berhasil diupdate");
      } else {
        await api.post("/promos/vouchers", voucherForm);
        toast.success("Voucher berhasil ditambahkan");
      }
      setShowVoucherModal(false);
      setEditVoucher(null);
      setVoucherForm({
        code: "",
        promo_id: "",
        discount_type: "percentage",
        discount_value: "",
        min_purchase: "0",
        max_uses: "1",
        valid_from: "",
        valid_until: "",
        is_active: 1,
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menyimpan voucher");
    }
  };

  const handleEditVoucher = (v) => {
    setEditVoucher(v);
    setVoucherForm({
      code: v.code,
      promo_id: v.promo_id || "",
      discount_type: v.discount_type,
      discount_value: v.discount_value,
      min_purchase: v.min_purchase || "0",
      max_uses: v.max_uses || "1",
      valid_from: v.valid_from || "",
      valid_until: v.valid_until || "",
      is_active: v.is_active,
    });
    setShowVoucherModal(true);
  };

  const handleDeleteVoucher = (v) => {
    openConfirm("Hapus Voucher", `Hapus voucher "${v.code}"?`, async () => {
      closeConfirm();
      try {
        await api.delete(`/promos/vouchers/${v.id}`);
        toast.success("Voucher berhasil dihapus");
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.message || "Gagal menghapus voucher");
      }
    });
  };

  // ========== Vehicle Type Handlers ==========
  const openVTModal = (vt = null) => {
    setEditVT(vt);
    setVtForm(
      vt
        ? {
            name: vt.name,
            requires_plate: vt.requires_plate,
            sort_order: vt.sort_order,
            is_active: vt.is_active,
          }
        : { name: "", requires_plate: 1, sort_order: 0, is_active: 1 },
    );
    setShowVTModal(true);
  };

  const handleVTSubmit = async () => {
    if (!vtForm.name) {
      toast.error("Nama wajib diisi");
      return;
    }
    try {
      if (editVT) {
        await api.put(`/settings/vehicle-types/${editVT.id}`, vtForm);
        toast.success("Jenis kendaraan berhasil diupdate");
      } else {
        await api.post("/settings/vehicle-types", vtForm);
        toast.success("Jenis kendaraan berhasil ditambahkan");
      }
      setShowVTModal(false);
      loadVehicleTypes();
    } catch (error) {
      toast.error(error.response?.data?.message || "Gagal menyimpan");
    }
  };

  const toggleVTActive = async (vt) => {
    try {
      await api.put(`/settings/vehicle-types/${vt.id}`, {
        ...vt,
        is_active: vt.is_active ? 0 : 1,
      });
      loadVehicleTypes();
    } catch (error) {
      toast.error("Gagal mengubah status");
    }
  };

  const deleteVT = (vt) => {
    openConfirm(
      "Hapus Jenis Kendaraan",
      `Hapus jenis kendaraan "${vt.name}"?`,
      async () => {
        closeConfirm();
        try {
          await api.delete(`/settings/vehicle-types/${vt.id}`);
          toast.success("Jenis kendaraan berhasil dihapus");
          loadVehicleTypes();
        } catch (error) {
          toast.error(error.response?.data?.message || "Gagal menghapus");
        }
      },
    );
  };

  const activeVehicleTypes = vehicleTypes.filter((vt) => vt.is_active);

  // ========== Loyalty Handlers ==========
  const handleToggleLoyalty = async (enabled) => {
    try {
      await api.put("/settings", { loyalty_enabled: enabled ? "1" : "0" });
      setLoyaltyEnabled(enabled);
      toast.success(enabled ? "Loyalty diaktifkan" : "Loyalty dinonaktifkan");
    } catch (error) {
      toast.error("Gagal mengubah status");
    }
  };

  const openRuleModal = (rule = null) => {
    setEditRule(rule);
    setRuleForm(
      rule
        ? {
            name: rule.name,
            vehicle_category_id: rule.vehicle_category_id
              ? String(rule.vehicle_category_id)
              : "",
            trigger_service_ids: rule.trigger_service_ids || "",
            required_count: String(rule.required_count),
            reward_service_id: rule.reward_service_id
              ? String(rule.reward_service_id)
              : "",
            redemption_mode: rule.redemption_mode || "manual",
            reward_item_type: rule.reward_item_type || "service",
            reward_product_id: rule.reward_product_id
              ? String(rule.reward_product_id)
              : "",
          }
        : {
            name: "",
            vehicle_category_id: "",
            trigger_service_ids: "",
            required_count: "10",
            reward_service_id: "",
            redemption_mode: "manual",
            reward_item_type: "service",
            reward_product_id: "",
          },
    );
    setShowRuleModal(true);
  };

  const handleSaveRule = async () => {
    const rewardMissing =
      ruleForm.reward_item_type === "product"
        ? !ruleForm.reward_product_id
        : !ruleForm.reward_service_id;
    if (
      !ruleForm.name ||
      !ruleForm.trigger_service_ids ||
      rewardMissing ||
      !ruleForm.required_count
    ) {
      toast.error("Nama, layanan trigger, jumlah, dan reward wajib diisi");
      return;
    }
    setLoyaltyLoading(true);
    try {
      if (editRule) {
        await api.put(`/settings/loyalty-rules/${editRule.id}`, {
          ...ruleForm,
          is_active: editRule.is_active,
        });
        toast.success("Rule berhasil diupdate");
      } else {
        await api.post("/settings/loyalty-rules", ruleForm);
        toast.success("Rule berhasil ditambahkan");
      }
      setShowRuleModal(false);
      loadLoyaltyRules();
    } catch (error) {
      toast.error("Gagal menyimpan rule");
    } finally {
      setLoyaltyLoading(false);
    }
  };

  const toggleRuleActive = async (rule) => {
    try {
      await api.put(`/settings/loyalty-rules/${rule.id}`, {
        ...rule,
        is_active: rule.is_active ? 0 : 1,
      });
      loadLoyaltyRules();
    } catch (error) {
      toast.error("Gagal mengubah status");
    }
  };

  const recalculateLoyalty = async () => {
    try {
      const res = await api.post("/settings/recalculate-loyalty");
      toast.success(res.data.message);
      loadLoyaltyRules();
    } catch (error) {
      toast.error("Gagal menghitung ulang loyalti");
    }
  };

  const handleDeleteRule = (rule) => {
    openConfirm("Hapus Rule", `Hapus rule "${rule.name}"?`, async () => {
      closeConfirm();
      try {
        await api.delete(`/settings/loyalty-rules/${rule.id}`);
        toast.success("Rule berhasil dihapus");
        loadLoyaltyRules();
      } catch (error) {
        toast.error("Gagal menghapus");
      }
    });
  };

  return (
    <div>
      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-4">
        {/* ===== TAB: LAYANAN ===== */}
        <Tab eventKey="services" title="Layanan">
          <div className="d-flex justify-content-end mb-3">
            <button className="btn btn-primary" onClick={openAddService}>
              <FiPlus className="me-1" /> Tambah Layanan
            </button>
          </div>
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Nama</th>
                    <th>Kategori</th>
                    <th className="text-end">Harga</th>
                    <th className="text-center">Durasi</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s) => (
                    <tr key={s.id}>
                      <td className="fw-bold">
                        {s.name}
                        {!!s.is_loyalty_reward && (
                          <Badge
                            bg="warning"
                            text="dark"
                            className="ms-2"
                            style={{ fontSize: "0.7em" }}
                          >
                            Reward
                          </Badge>
                        )}
                      </td>
                      <td>{s.category_name || "-"}</td>
                      <td className="text-end">{fmt(s.price)}</td>
                      <td className="text-center">
                        {s.duration_minutes} menit
                      </td>
                      <td>
                        <span
                          className={`badge ${s.is_active ? "bg-success" : "bg-danger"}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleServiceActive(s)}
                          title={
                            s.is_active
                              ? "Klik untuk nonaktifkan"
                              : "Klik untuk aktifkan"
                          }
                        >
                          {s.is_active ? "Aktif" : "Nonaktif"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEdit(s)}
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteService(s)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {services.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        Belum ada data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>

        {/* ===== TAB: KATEGORI LAYANAN ===== */}
        <Tab eventKey="service-categories" title="Kategori Layanan">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted mb-0 small">
              Kelompok layanan (misal: Cuci Reguler, Detailing, dll).
            </p>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => openCatModal()}
            >
              <FiPlus className="me-1" /> Tambah Kategori
            </button>
          </div>
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Nama</th>
                    <th>Deskripsi</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr key={cat.id}>
                      <td className="fw-bold">{cat.name}</td>
                      <td className="text-muted small">
                        {cat.description || "—"}
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openCatModal(cat)}
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteCat(cat)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {categories.length === 0 && (
                    <tr>
                      <td colSpan="3" className="text-center text-muted py-4">
                        Belum ada kategori layanan
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>

        {/* ===== TAB: PROMO ===== */}
        <Tab eventKey="promos" title="Promo">
          <div className="d-flex justify-content-end mb-3">
            <button
              className="btn btn-primary"
              onClick={() => {
                setEditPromo(null);
                setPromoForm({
                  name: "",
                  description: "",
                  type: "percentage",
                  value: "",
                  min_purchase: "0",
                  applies_to: "all",
                  start_date: "",
                  end_date: "",
                  is_active: 1,
                });
                setShowPromoModal(true);
              }}
            >
              <FiPlus className="me-1" /> Tambah Promo
            </button>
          </div>
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Nama</th>
                    <th>Tipe</th>
                    <th>Nilai</th>
                    <th>Berlaku Untuk</th>
                    <th>Periode</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {promos.map((p) => (
                    <tr key={p.id}>
                      <td className="fw-bold">{p.name}</td>
                      <td>{p.type}</td>
                      <td>
                        {p.type === "percentage" ? `${p.value}%` : fmt(p.value)}
                      </td>
                      <td>{p.applies_to}</td>
                      <td>
                        {p.start_date || "-"} s/d {p.end_date || "-"}
                      </td>
                      <td>
                        <span
                          className={`badge ${p.is_active ? "bg-success" : "bg-secondary"}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => togglePromoActive(p)}
                          title={
                            p.is_active
                              ? "Klik untuk nonaktifkan"
                              : "Klik untuk aktifkan"
                          }
                        >
                          {p.is_active ? "Aktif" : "Nonaktif"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditPromo(p)}
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeletePromo(p)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {promos.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        Belum ada promo
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>

        {/* ===== TAB: VOUCHER ===== */}
        <Tab eventKey="vouchers" title="Voucher">
          <div className="d-flex justify-content-end mb-3">
            <button
              className="btn btn-primary"
              onClick={() => {
                setEditVoucher(null);
                setVoucherForm({
                  code: "",
                  promo_id: "",
                  discount_type: "percentage",
                  discount_value: "",
                  min_purchase: "0",
                  max_uses: "1",
                  valid_from: "",
                  valid_until: "",
                  is_active: 1,
                });
                setShowVoucherModal(true);
              }}
            >
              <FiPlus className="me-1" /> Tambah Voucher
            </button>
          </div>
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Kode</th>
                    <th>Diskon</th>
                    <th>Min. Belanja</th>
                    <th>Penggunaan</th>
                    <th>Berlaku</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {vouchers.map((v) => (
                    <tr key={v.id}>
                      <td>
                        <code className="fw-bold">{v.code}</code>
                      </td>
                      <td>
                        {v.discount_type === "percentage"
                          ? `${v.discount_value}%`
                          : fmt(v.discount_value)}
                      </td>
                      <td>{fmt(v.min_purchase)}</td>
                      <td>
                        {v.used_count} / {v.max_uses}
                      </td>
                      <td>
                        {v.valid_from || "-"} s/d {v.valid_until || "-"}
                      </td>
                      <td>
                        <span
                          className={`badge ${v.is_active ? "bg-success" : "bg-secondary"}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleVoucherActive(v)}
                          title={
                            v.is_active
                              ? "Klik untuk nonaktifkan"
                              : "Klik untuk aktifkan"
                          }
                        >
                          {v.is_active ? "Aktif" : "Nonaktif"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleEditVoucher(v)}
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteVoucher(v)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {vouchers.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        Belum ada voucher
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>

        {/* ===== TAB: JENIS KENDARAAN ===== */}
        <Tab eventKey="vehicles" title="Kendaraan">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted mb-0">
              Master data jenis kendaraan yang tersedia untuk customer.
            </p>
            <button className="btn btn-primary" onClick={() => openVTModal()}>
              <FiPlus className="me-1" /> Tambah
            </button>
          </div>
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Nama</th>
                    <th>Wajib Plat</th>
                    <th>Urutan</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {vehicleTypes.map((vt) => (
                    <tr
                      key={vt.id}
                      className={!vt.is_active ? "text-muted" : ""}
                    >
                      <td className="fw-bold">{vt.name}</td>
                      <td>
                        {vt.requires_plate ? (
                          <Badge bg="info">Ya</Badge>
                        ) : (
                          <Badge bg="secondary">Tidak</Badge>
                        )}
                      </td>
                      <td>{vt.sort_order}</td>
                      <td>
                        <span
                          className={`badge ${vt.is_active ? "bg-success" : "bg-secondary"}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleVTActive(vt)}
                        >
                          {vt.is_active ? "Aktif" : "Nonaktif"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openVTModal(vt)}
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => deleteVT(vt)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {vehicleTypes.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-4">
                        Belum ada data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>

        {/* ===== TAB: PROGRAM LOYALTI ===== */}
        <Tab eventKey="loyalty" title="Loyalti">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-3">
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={loyaltyEnabled}
                  onChange={(e) => handleToggleLoyalty(e.target.checked)}
                />
                <label className="form-check-label fw-bold">
                  Program Loyalti {loyaltyEnabled ? "Aktif" : "Nonaktif"}
                </label>
              </div>
              <button
                className="btn btn-sm btn-outline-warning"
                onClick={recalculateLoyalty}
                title="Hitung ulang progress loyalti dari seluruh riwayat transaksi"
              >
                Hitung Ulang Progress
              </button>
            </div>
            <button className="btn btn-primary" onClick={() => openRuleModal()}>
              <FiPlus className="me-1" /> Tambah Rule
            </button>
          </div>
          <p className="text-muted small mb-3">
            Atur aturan loyalti: layanan A sebanyak X kali → gratis layanan B 1
            kali.
          </p>
          <div className="card">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Nama Rule</th>
                    <th>Kategori Layanan</th>
                    <th>Layanan Trigger</th>
                    <th className="text-center">Jumlah</th>
                    <th>Layanan Reward</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {loyaltyRules.map((rule) => (
                    <tr
                      key={rule.id}
                      className={!rule.is_active ? "text-muted" : ""}
                    >
                      <td className="fw-bold">{rule.name}</td>
                      <td>
                        {rule.vehicle_category_name ? (
                          <span className="badge bg-info">
                            {rule.vehicle_category_name}
                          </span>
                        ) : (
                          <span className="text-muted small">Semua</span>
                        )}
                      </td>
                      <td className="small">{rule.trigger_service_names}</td>
                      <td className="text-center">
                        <Badge bg="info">{rule.required_count}x</Badge>
                      </td>
                      <td>
                        <span className="text-success fw-bold">
                          {rule.reward_name}
                        </span>
                        {rule.reward_price > 0 && (
                          <span className="text-muted ms-1 small">
                            ({fmt(rule.reward_price)})
                          </span>
                        )}
                        <div>
                          {rule.reward_item_type === "product" && (
                            <span className="badge bg-warning text-dark me-1">
                              Produk
                            </span>
                          )}
                          {rule.redemption_mode === "auto" && (
                            <span className="badge bg-primary">Auto</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge ${rule.is_active ? "bg-success" : "bg-secondary"}`}
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleRuleActive(rule)}
                        >
                          {rule.is_active ? "Aktif" : "Nonaktif"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openRuleModal(rule)}
                          >
                            <FiEdit2 size={14} />
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDeleteRule(rule)}
                          >
                            <FiTrash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {loyaltyRules.length === 0 && (
                    <tr>
                      <td colSpan="7" className="text-center text-muted py-4">
                        Belum ada rule loyalti
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Tab>
      </Tabs>

      {/* ===== MODALS ===== */}

      {/* Service Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editItem ? "Edit Layanan" : "Tambah Layanan"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Kategori</Form.Label>
            <Form.Select
              value={form.category_id}
              onChange={(e) =>
                setForm({ ...form, category_id: e.target.value })
              }
            >
              <option value="">Pilih Kategori Layanan</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Nama Layanan *</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Harga *</Form.Label>
            <Form.Control
              type="number"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
            />
          </Form.Group>
          <div className="row g-2">
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Durasi (menit)</Form.Label>
                <Form.Control
                  type="number"
                  value={form.duration_minutes}
                  onChange={(e) =>
                    setForm({ ...form, duration_minutes: e.target.value })
                  }
                />
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-2">
            <Form.Label>Deskripsi</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </Form.Group>
          {editItem && (
            <Form.Check
              type="switch"
              label={`Status: ${form.is_active ? "Aktif" : "Nonaktif"}`}
              checked={form.is_active === 1}
              onChange={(e) =>
                setForm({ ...form, is_active: e.target.checked ? 1 : 0 })
              }
              className="mt-2"
            />
          )}
          <Form.Check
            type="checkbox"
            label="Layanan hadiah loyalty (tidak tampil di katalog POS)"
            checked={!!form.is_loyalty_reward}
            onChange={(e) =>
              setForm({ ...form, is_loyalty_reward: e.target.checked ? 1 : 0 })
            }
            className="mt-2"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Service Category Modal */}
      <Modal show={showCatModal} onHide={() => setShowCatModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editCat ? "Edit Kategori Layanan" : "Tambah Kategori Layanan"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nama Kategori *</Form.Label>
            <Form.Control
              value={catForm.name}
              onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
              placeholder="Contoh: Cuci Reguler, Detailing, ..."
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Deskripsi</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={catForm.description}
              onChange={(e) =>
                setCatForm({ ...catForm, description: e.target.value })
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCatModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleSaveCat}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Promo Modal */}
      <Modal
        show={showPromoModal}
        onHide={() => {
          setShowPromoModal(false);
          setEditPromo(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{editPromo ? "Edit Promo" : "Tambah Promo"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Nama</Form.Label>
            <Form.Control
              value={promoForm.name}
              onChange={(e) =>
                setPromoForm({ ...promoForm, name: e.target.value })
              }
            />
          </Form.Group>
          <div className="row g-2">
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Tipe</Form.Label>
                <Form.Select
                  value={promoForm.type}
                  onChange={(e) =>
                    setPromoForm({ ...promoForm, type: e.target.value })
                  }
                >
                  <option value="percentage">Persentase</option>
                  <option value="fixed">Nominal</option>
                  <option value="bundling">Bundling</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Nilai</Form.Label>
                <Form.Control
                  type="number"
                  value={promoForm.value}
                  onChange={(e) =>
                    setPromoForm({ ...promoForm, value: e.target.value })
                  }
                />
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-2">
            <Form.Label>Berlaku Untuk</Form.Label>
            <Form.Select
              value={promoForm.applies_to}
              onChange={(e) =>
                setPromoForm({ ...promoForm, applies_to: e.target.value })
              }
            >
              <option value="all">Semua</option>
              <option value="service">Layanan</option>
              <option value="product">Produk</option>
            </Form.Select>
          </Form.Group>
          <div className="row g-2">
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Mulai</Form.Label>
                <Form.Control
                  type="date"
                  value={promoForm.start_date}
                  onChange={(e) =>
                    setPromoForm({ ...promoForm, start_date: e.target.value })
                  }
                />
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Berakhir</Form.Label>
                <Form.Control
                  type="date"
                  value={promoForm.end_date}
                  onChange={(e) =>
                    setPromoForm({ ...promoForm, end_date: e.target.value })
                  }
                />
              </Form.Group>
            </div>
          </div>
          <Form.Group className="mb-2">
            <Form.Label>Deskripsi</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={promoForm.description}
              onChange={(e) =>
                setPromoForm({ ...promoForm, description: e.target.value })
              }
            />
          </Form.Group>
          <Form.Check
            type="switch"
            label={`Status: ${promoForm.is_active ? "Aktif" : "Nonaktif"}`}
            checked={promoForm.is_active === 1}
            onChange={(e) =>
              setPromoForm({
                ...promoForm,
                is_active: e.target.checked ? 1 : 0,
              })
            }
            className="mt-2"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowPromoModal(false);
              setEditPromo(null);
            }}
          >
            Batal
          </Button>
          <Button variant="primary" onClick={handlePromoSubmit}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Voucher Modal */}
      <Modal
        show={showVoucherModal}
        onHide={() => {
          setShowVoucherModal(false);
          setEditVoucher(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editVoucher ? "Edit Voucher" : "Tambah Voucher"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Kode Voucher *</Form.Label>
            <Form.Control
              value={voucherForm.code}
              onChange={(e) =>
                setVoucherForm({
                  ...voucherForm,
                  code: e.target.value.toUpperCase(),
                })
              }
            />
          </Form.Group>
          <div className="row g-2">
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Tipe Diskon</Form.Label>
                <Form.Select
                  value={voucherForm.discount_type}
                  onChange={(e) =>
                    setVoucherForm({
                      ...voucherForm,
                      discount_type: e.target.value,
                    })
                  }
                >
                  <option value="percentage">Persentase</option>
                  <option value="fixed">Nominal</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Nilai</Form.Label>
                <Form.Control
                  type="number"
                  value={voucherForm.discount_value}
                  onChange={(e) =>
                    setVoucherForm({
                      ...voucherForm,
                      discount_value: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </div>
          </div>
          <div className="row g-2">
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Min. Belanja</Form.Label>
                <Form.Control
                  type="number"
                  value={voucherForm.min_purchase}
                  onChange={(e) =>
                    setVoucherForm({
                      ...voucherForm,
                      min_purchase: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Maks. Penggunaan</Form.Label>
                <Form.Control
                  type="number"
                  value={voucherForm.max_uses}
                  onChange={(e) =>
                    setVoucherForm({ ...voucherForm, max_uses: e.target.value })
                  }
                />
              </Form.Group>
            </div>
          </div>
          <div className="row g-2">
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Berlaku Dari</Form.Label>
                <Form.Control
                  type="date"
                  value={voucherForm.valid_from}
                  onChange={(e) =>
                    setVoucherForm({
                      ...voucherForm,
                      valid_from: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </div>
            <div className="col-6">
              <Form.Group className="mb-2">
                <Form.Label>Berlaku Sampai</Form.Label>
                <Form.Control
                  type="date"
                  value={voucherForm.valid_until}
                  onChange={(e) =>
                    setVoucherForm({
                      ...voucherForm,
                      valid_until: e.target.value,
                    })
                  }
                />
              </Form.Group>
            </div>
          </div>
          <Form.Check
            type="switch"
            label={`Status: ${voucherForm.is_active ? "Aktif" : "Nonaktif"}`}
            checked={voucherForm.is_active === 1}
            onChange={(e) =>
              setVoucherForm({
                ...voucherForm,
                is_active: e.target.checked ? 1 : 0,
              })
            }
            className="mt-2"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowVoucherModal(false);
              setEditVoucher(null);
            }}
          >
            Batal
          </Button>
          <Button variant="primary" onClick={handleVoucherSubmit}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Vehicle Type Modal */}
      <Modal
        show={showVTModal}
        onHide={() => setShowVTModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editVT ? "Edit" : "Tambah"} Jenis Kendaraan
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nama *</Form.Label>
            <Form.Control
              value={vtForm.name}
              onChange={(e) => setVtForm({ ...vtForm, name: e.target.value })}
              placeholder="Contoh: Mobil"
            />
          </Form.Group>
          <Form.Check
            type="switch"
            label="Wajib isi plat nomor"
            checked={vtForm.requires_plate === 1}
            onChange={(e) =>
              setVtForm({ ...vtForm, requires_plate: e.target.checked ? 1 : 0 })
            }
            className="mb-3"
          />
          <Form.Group className="mb-3">
            <Form.Label>Urutan</Form.Label>
            <Form.Control
              type="number"
              value={vtForm.sort_order}
              onChange={(e) =>
                setVtForm({
                  ...vtForm,
                  sort_order: parseInt(e.target.value) || 0,
                })
              }
            />
          </Form.Group>
          <Form.Check
            type="switch"
            label={`Status: ${vtForm.is_active ? "Aktif" : "Nonaktif"}`}
            checked={vtForm.is_active === 1}
            onChange={(e) =>
              setVtForm({ ...vtForm, is_active: e.target.checked ? 1 : 0 })
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVTModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleVTSubmit}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Loyalty Rule Modal */}
      <Modal
        show={showRuleModal}
        onHide={() => setShowRuleModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{editRule ? "Edit" : "Tambah"} Rule Loyalti</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nama Program *</Form.Label>
            <Form.Control
              value={ruleForm.name}
              onChange={(e) =>
                setRuleForm({ ...ruleForm, name: e.target.value })
              }
              placeholder="Contoh: Cuci Mobil Kecil 10x Gratis 1x"
            />
          </Form.Group>
          {!editRule && (
            <Form.Group className="mb-3">
              <Form.Label>Kategori Layanan</Form.Label>
              <Form.Select
                value={ruleForm.vehicle_category_id}
                onChange={(e) =>
                  setRuleForm({
                    ...ruleForm,
                    vehicle_category_id: e.target.value,
                    trigger_service_ids: "",
                    reward_service_id: "",
                  })
                }
              >
                <option value="">Semua Kategori</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Filter layanan berdasarkan kategori ini.
              </Form.Text>
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Layanan Trigger (yang dihitung) *</Form.Label>
            {!editRule && !ruleForm.vehicle_category_id ? (
              <p className="text-muted small border rounded p-2">
                ← Pilih kategori layanan terlebih dahulu
              </p>
            ) : (
              <div
                className="border rounded p-2"
                style={{ maxHeight: "160px", overflowY: "auto" }}
              >
                {services
                  .filter(
                    (s) =>
                      !s.is_loyalty_reward &&
                      (!ruleForm.vehicle_category_id ||
                        String(s.category_id) ===
                          String(ruleForm.vehicle_category_id)),
                  )
                  .map((s) => {
                    const selected = (ruleForm.trigger_service_ids || "")
                      .split(",")
                      .map((t) => t.trim())
                      .filter(Boolean);
                    const isChecked = selected.includes(String(s.id));
                    return (
                      <div className="form-check" key={s.id}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            let ids = selected.filter(
                              (t) => t !== String(s.id),
                            );
                            if (e.target.checked) ids.push(String(s.id));
                            setRuleForm({
                              ...ruleForm,
                              trigger_service_ids: ids.join(","),
                            });
                          }}
                          id={`ts-${s.id}`}
                        />
                        <label
                          className="form-check-label small"
                          htmlFor={`ts-${s.id}`}
                        >
                          {s.name} ({fmt(s.price)})
                        </label>
                      </div>
                    );
                  })}
              </div>
            )}
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Jumlah Transaksi untuk Reward *</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={ruleForm.required_count}
              onChange={(e) =>
                setRuleForm({ ...ruleForm, required_count: e.target.value })
              }
            />
            <Form.Text className="text-muted">
              Setelah {ruleForm.required_count || "N"}x layanan trigger,
              customer mendapat reward.
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Mode Klaim Reward *</Form.Label>
            <Form.Select
              value={ruleForm.redemption_mode}
              onChange={(e) =>
                setRuleForm({ ...ruleForm, redemption_mode: e.target.value })
              }
            >
              <option value="manual">
                Manual (diklaim di transaksi berikutnya)
              </option>
              <option value="auto">
                Otomatis (langsung gratis di transaksi saat syarat terpenuhi)
              </option>
            </Form.Select>
            <Form.Text className="text-muted">
              {ruleForm.redemption_mode === "auto"
                ? "Reward otomatis ditambahkan (gratis) begitu counter mencapai target."
                : "Kasir memilih reward secara manual pada transaksi berikutnya."}
            </Form.Text>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Jenis Reward *</Form.Label>
            <Form.Select
              value={ruleForm.reward_item_type}
              onChange={(e) =>
                setRuleForm({
                  ...ruleForm,
                  reward_item_type: e.target.value,
                  reward_service_id: "",
                  reward_product_id: "",
                })
              }
            >
              <option value="service">Layanan</option>
              <option value="product">Produk</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Reward (diberikan gratis) *</Form.Label>
            {ruleForm.reward_item_type === "product" ? (
              <Form.Select
                value={ruleForm.reward_product_id}
                onChange={(e) =>
                  setRuleForm({
                    ...ruleForm,
                    reward_product_id: e.target.value,
                  })
                }
              >
                <option value="">-- Pilih Produk --</option>
                {products
                  .filter((p) => p.is_active)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({fmt(p.sell_price)})
                    </option>
                  ))}
              </Form.Select>
            ) : !editRule && !ruleForm.vehicle_category_id ? (
              <p className="text-muted small border rounded p-2">
                ← Pilih kategori layanan terlebih dahulu
              </p>
            ) : (
              <Form.Select
                value={ruleForm.reward_service_id}
                onChange={(e) =>
                  setRuleForm({
                    ...ruleForm,
                    reward_service_id: e.target.value,
                  })
                }
              >
                <option value="">-- Pilih Layanan --</option>
                {services
                  .filter(
                    (s) =>
                      !ruleForm.vehicle_category_id ||
                      String(s.category_id) ===
                        String(ruleForm.vehicle_category_id),
                  )
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({fmt(s.price)})
                    </option>
                  ))}
              </Form.Select>
            )}
            <Form.Text className="text-muted">
              {ruleForm.reward_item_type === "product" ? "Produk" : "Layanan"}{" "}
              yang diberikan gratis sebagai reward.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRuleModal(false)}>
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveRule}
            disabled={loyaltyLoading}
          >
            {loyaltyLoading ? "Menyimpan..." : "Simpan"}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default Services;
